import { RadioLayout } from "@tree-company/tree-query";
import { PageComponent } from "./components/page_component";
import { TellitPage } from "./components/tellit_page";
import { CollapsibleRadio } from "./components/collapsible_radio";
import { NextStreet } from "./views/next_street";
import { apiClient } from "./client";
import {
  closedQuestionForStreet,
  flowForKey,
  hasClosedQuestion,
  hasScenarioChoice,
  openQuestionForStreet,
  scenarioOptionsForStreet,
  streetForFlow,
  streetsForFlow,
} from "./flows";
import { translatedPropertyFromKey } from "./locales";

let aggregatorURL = import.meta.env.VITE_AGGREGATOR_URL;
if (aggregatorURL === undefined && import.meta.env.DEV) {
  aggregatorURL = "http://localhost:3300";
}

function getPreviousAnswer(key) {
  // We select the second to last one, since the last treesponse is the current one
  return apiClient.treesponses.at(-2)?.getAnswer(key);
}

function getAnyPreviousAnswer(key) {
  // Search all previous treesponses (excluding the current one) for an answer
  const previous = apiClient.treesponses.slice(0, -1);
  for (let i = previous.length - 1; i >= 0; i--) {
    const answer = previous[i].getAnswer(key);
    if (answer !== undefined) return answer;
  }
  return undefined;
}

function hasNameChanged() {
  const currentName = apiClient.treesponses.at(-1)?.getAnswer("firstName");
  const previousName = getPreviousAnswer("firstName");
  if (previousName === undefined) return false;
  return currentName !== previousName;
}

const aiResponseTexts = {
  proficiencyOkToxicityOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_ok_toxicity_ok",
  ),
  proficiencyOkToxicityNotOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_ok_toxicity_not_ok",
  ),
  proficiencyHighToxicityOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_high_toxicity_ok",
  ),
  proficiencyHighToxicityNotOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_high_toxicity_not_ok",
  ),
  proficiencyLowToxicityOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_low_toxicity_ok",
  ),
  proficiencyLowToxicityNotOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_low_toxicity_not_ok",
  ),
};

function hasCompletedOptin(flowKey) {
  return apiClient.treesponses.some((treesponse) => {
    if (treesponse.getAnswer("flow") !== flowKey) return false;

    return (
      treesponse.getAnswer("project_opt_in") !== undefined ||
      treesponse.getAnswer("optinBpact") !== undefined
    );
  });
}

function streetOptions(flowKey) {
  return streetsForFlow(flowKey).map((street) => ({
    value: street.key,
    label: street.name,
  }));
}

function translated(i18n, text) {
  return { [i18n.locale]: text };
}

function translatedFromKey(i18n, key) {
  return translated(i18n, i18n.t(key));
}

function openQuestionDescription(flowKey, treesponse, i18n) {
  const street = treesponse.getAnswer("street");
  const scenario = treesponse.getAnswer("scenario_choice");
  const oq = openQuestionForStreet(flowKey, street, scenario);
  return i18n.t(oq.description);
}

export function bpartTitleForStreet(flowKey, streetKey, i18n) {
  const streetData = streetForFlow(flowKey, streetKey);
  return streetData ? i18n.t(streetData.name) : streetKey;
}

export function surveySettingsForFlow(flowKey, i18n) {
  const flow = flowForKey(flowKey);
  if (flow === undefined) {
    throw new Error(`Unknown flow: ${flowKey}`);
  }
  const t = (key) => translatedFromKey(i18n, key);

  return {
    key: `herent-mobility-${flowKey}`,
    pages: [
      {
        key: "street",
        order: 1,
        component: PageComponent,
        skipIf: (treesponse) => treesponse.getAnswer("street") !== undefined,
        htmlAttributes: { class: "survey-page--street" },
        title: t("street.intro.title"),
        sections: [
          {
            order: 1,
            type: "radio",
            key: "street",
            options: streetOptions(flowKey).map((option) => ({
              value: option.value,
              label: t(option.label),
            })),
            required: true,
          },
          {
            order: 99,
            type: "hidden",
            key: "bpart_title",
            dependsOn: ["street"],
            computeValue: (treesponse) => {
              const street = treesponse.getAnswer("street");
              return bpartTitleForStreet(flowKey, street, i18n);
            },
          },
        ],
      },
      {
        key: "tellit",
        order: 2,
        component: TellitPage,
        skipIf: (treesponse) => {
          const street = treesponse.getAnswer("street");
          return !hasScenarioChoice(flowKey, street);
        },
        sections: [
          {
            order: 1,
            type: "radio",
            key: "scenario_choice",
            layout: RadioLayout.buttons,
            title: t("scenario.title"),
            options: (treesponse) => {
              const street = treesponse.getAnswer("street");

              return scenarioOptionsForStreet(flowKey, street).map(
                (option) => ({
                  value: option.value,
                  label: t(option.label),
                }),
              );
            },
            required: true,
            htmlAttributes: {
              class: "question--scenario",
            },
            skipIf: (treesponse) => {
              const street = treesponse.getAnswer("street");
              return !hasScenarioChoice(flowKey, street);
            },
          },
        ],
      },
      {
        key: "closed",
        order: 3,
        component: TellitPage,
        skipIf: (treesponse) => {
          const street = treesponse.getAnswer("street");
          return !hasClosedQuestion(flowKey, street);
        },
        sections: [
          {
            order: 1,
            type: "radio",
            key: "closed_question",
            layout: RadioLayout.buttons,
            title: {
              [i18n.locale]: (treesponse) => {
                const street = treesponse.getAnswer("street");
                const scenario = treesponse.getAnswer("scenario_choice");
                const cq = closedQuestionForStreet(flowKey, street, scenario);
                return i18n.t(cq.title);
              },
            },
            description: {
              [i18n.locale]: (treesponse) => {
                const street = treesponse.getAnswer("street");
                const scenario = treesponse.getAnswer("scenario_choice");
                const cq = closedQuestionForStreet(flowKey, street, scenario);
                return i18n.t(cq.description);
              },
            },
            options: (treesponse) => {
              const street = treesponse.getAnswer("street");
              const scenario = treesponse.getAnswer("scenario_choice");
              const cq = closedQuestionForStreet(flowKey, street, scenario);
              return cq.options.map((option) => ({
                value: option.value,
                label: t(option.label),
              }));
            },
            required: true,
            htmlAttributes: {
              class: "question--tellit",
            },
            skipIf: (treesponse) => {
              const street = treesponse.getAnswer("street");
              return !hasClosedQuestion(flowKey, street);
            },
          },
          {
            order: 99,
            type: "hidden",
            key: "bpart_opinion",
            dependsOn: ["closed_question"],
            computeValue: (treesponse) => {
              const street = treesponse.getAnswer("street");
              const scenario = treesponse.getAnswer("scenario_choice");
              const opinion = treesponse.getAnswer("closed_question");
              if (opinion === undefined) return undefined;

              if (scenario === "scenario_1" || scenario === "scenario_2") {
                return i18n.t([
                  "flows",
                  flowKey,
                  "streets",
                  street,
                  "scenarios",
                  scenario,
                  "closed_question",
                  "options",
                  opinion,
                ]);
              } else {
                return i18n.t([
                  "flows",
                  flowKey,
                  "streets",
                  street,
                  "closed_question",
                  "options",
                  opinion,
                ]);
              }
            },
          },
        ],
      },
      {
        key: "open",
        order: 4,
        component: PageComponent,
        title: {
          [i18n.locale]: (treesponse) => {
            const street = treesponse.getAnswer("street");
            const scenario = treesponse.getAnswer("scenario_choice");
            const oq = openQuestionForStreet(flowKey, street, scenario);
            return i18n.t(oq.title);
          },
        },
        description: {
          [i18n.locale]: (treesponse) =>
            openQuestionDescription(flowKey, treesponse, i18n),
        },
        sections: [
          {
            order: 1,
            type: "text",
            key: "comment",
            placeholder: t("open_question.placeholder"),
            aiResponseTexts,
            maxLength: 500,
            required: true,
          },
          {
            order: 99,
            type: "hidden",
            key: "bpart_path",
            dependsOn: ["street"],
            computeValue: (treesponse) => {
              const street = treesponse.getAnswer("street");
              return streetForFlow(flowKey, street)?.bpartPath || street;
            },
          },
        ],
      },
      {
        key: "name",
        order: 5,
        component: PageComponent,
        description: t("name.intro.description"),
        sections: [
          {
            order: 1,
            type: "text",
            key: "firstName",
            title: t("name.firstName.title"),
            required: true,
            defaultValue: () => getPreviousAnswer("firstName"),
          },
          {
            order: 99,
            type: "hidden",
            key: "completed",
            defaultValue: true,
          },
        ],
      },
      {
        key: "sociodemo",
        order: 6,
        component: PageComponent,
        skipIf: () =>
          !hasNameChanged() &&
          getAnyPreviousAnswer("age_category") !== undefined &&
          getAnyPreviousAnswer("woonplaats") !== undefined &&
          getAnyPreviousAnswer("handelaar") !== undefined,
        title: t("sociodemo.intro.title"),
        description: t("sociodemo.intro.description"),
        sections: [
          {
            order: 1,
            type: "radio",
            key: "age_category",
            component: CollapsibleRadio,
            title: t("sociodemo.age_category.title"),
            skipIf: () =>
              !hasNameChanged() &&
              getAnyPreviousAnswer("age_category") !== undefined,
            options: [
              "u18",
              "18_24",
              "25_34",
              "35_44",
              "45_54",
              "55_64",
              "65_plus",
            ].map((value) => ({
              value,
              label: t(["sociodemo", "age_category", "options", value]),
            })),
            defaultValue: () => getPreviousAnswer("age_category"),
          },
          {
            order: 2,
            type: "radio",
            key: "woonplaats",
            component: CollapsibleRadio,
            title: t("sociodemo.woonplaats.title"),
            skipIf: () =>
              !hasNameChanged() &&
              getAnyPreviousAnswer("woonplaats") !== undefined,
            options: [
              "herent",
              "veltem_beisem",
              "winksele_deile",
              "outside_herent",
            ].map((value) => ({
              value,
              label: t(["sociodemo", "woonplaats", "options", value]),
            })),
            defaultValue: () => getPreviousAnswer("woonplaats"),
          },
          {
            order: 3,
            type: "radio",
            key: "handelaar",
            component: CollapsibleRadio,
            title: t("sociodemo.handelaar.title"),
            skipIf: () =>
              !hasNameChanged() &&
              getAnyPreviousAnswer("handelaar") !== undefined,
            options: ["yes", "no"].map((value) => ({
              value,
              label: t(["sociodemo", "handelaar", "options", value]),
            })),
            defaultValue: () => getPreviousAnswer("handelaar"),
          },
        ],
      },
      {
        key: "optin",
        order: 7,
        component: PageComponent,
        title: t("optin.intro.title"),
        skipIf: () => hasCompletedOptin(flowKey),
        sections: [
          {
            order: 1,
            type: "checkbox",
            key: "project_opt_in",
            title: t("optin.project.title"),
          },
          {
            order: 2,
            type: "checkbox",
            key: "optinBpact",
            title: t("optin.bpact.title"),
          },
          {
            order: 3,
            type: "email",
            key: "email",
            title: t("optin.email.title"),
            dependsOn: ["project_opt_in", "optinBpact"],
            skipIf: (treesponse) =>
              treesponse.getAnswer("project_opt_in") !== true &&
              treesponse.getAnswer("optinBpact") !== true,
            customValidations: [
              (treesponse, _key, value) => {
                const needsEmail =
                  treesponse.getAnswer("project_opt_in") === true ||
                  treesponse.getAnswer("optinBpact") === true;
                if (needsEmail && (value === null || value === undefined)) {
                  return [false, t("optin.email.required")];
                }

                return [true, null];
              },
            ],
          },
        ],
      },
      {
        key: "like",
        order: 8,
        component: PageComponent,
        title: t("like.title"),
        description: t("like.description"),
        sections: [
          {
            order: 1,
            type: "like",
            key: "like",
            aggregatorSettings: {
              url: `${aggregatorURL}/herent-mobility`,
              projectKey: import.meta.env.VITE_AGGREGATOR_KEY,
            },
          },
          {
            order: 99,
            type: "hidden",
            key: "completed",
            defaultValue: true,
          },
        ],
      },
      {
        key: "next",
        order: 9,
        component: NextStreet,
        sections: [],
      },
    ],
  };
}
